import { useState } from "react";
import { observer } from "mobx-react-lite";
import { ActionHandler } from "@/components/UI/tools";
import { EmptyRow, Field, Pill, Row, Section, TextField } from "@/app/workspace/settings/ui";
import { Add_new, Remove } from "@/components/UI/svgs";
import { Request } from "@/components/CRUD";
import { appState, UserSkill } from "@/app/appState";
import {
    createUserSkill,
    updateUserSkill,
    deleteUserSkill,
    CreateUserSkillData,
    UpdateUserSkillData,
    DeleteUserSkillData,
} from "@/api";

const SKILL_KEY_RE = /^[A-Za-z0-9_]{1,64}$/;

const overridesSystem = (key: string) =>
    appState.systemSkillKeys.some((k) => k.toLowerCase() === key.trim().toLowerCase());

const showError = (error: any) => {
    appState.setModal({
        title: appState.t("common.error"),
        body: error?.detail?.toString() ?? appState.t("common.somethingWentWrong"),
    });
};

export const SkillEditor = observer(({ skill, onClose, onSaved }: {
    skill: UserSkill | null; // null = create new
    onClose: () => void;
    onSaved: () => void;
}) => {
    const [skillKey, setSkillKey] = useState(skill?.skill_key ?? "");
    const [command, setCommand] = useState(skill?.command ?? "");
    const [useCase, setUseCase] = useState(skill?.use_case ?? "");
    const [prompt, setPrompt] = useState(skill?.prompt ?? "");
    const [createsNote, setCreatesNote] = useState(skill?.creates_note ?? false);
    const [noteTitle, setNoteTitle] = useState(skill?.note_title ?? "");
    const [noteContent, setNoteContent] = useState(skill?.note_content ?? "");
    const [loading, setLoading] = useState(false);

    const fields = () => ({
        command,
        use_case: useCase,
        prompt,
        creates_note: createsNote,
        note_title: createsNote ? noteTitle : "",
        note_content: createsNote ? noteContent : "",
    });
    const valid = prompt.trim().length > 0 && (skill !== null || SKILL_KEY_RE.test(skillKey.trim()));
    const done = () => { onSaved(); onClose(); };

    const create = Request<CreateUserSkillData, any>(
        createUserSkill,
        () => ({ body: { skill_key: skillKey.trim(), ...fields() } }),
        setLoading,
        done,
        showError,
        () => valid,
    );

    const update = Request<UpdateUserSkillData, any>(
        updateUserSkill,
        () => ({ body: { id: skill?.id ?? 0, ...fields() } }),
        setLoading,
        done,
        showError,
        () => valid,
    );

    const save = () => skill ? update(undefined as any) : create(undefined as any);
    const key = skill?.skill_key ?? skillKey;

    return (
        <div className="flex flex-col gap-2 min-w-[300px] w-[36rem] max-w-full">
            <Field label={appState.t("profile.skillKey")}>
                {skill ? (
                    <span className="flex flex-row items-center gap-1">
                        <code className="code-chip code-chip-muted">{skill.skill_key}</code>
                        {skill.overrides_system && <Pill tone="accent">{appState.t("profile.skillOverridesSystem")}</Pill>}
                    </span>
                ) : (
                    <>
                        <TextField value={skillKey} onChange={setSkillKey} placeholder="session_note" autoFocus />
                        <p className="help-text">{appState.t("profile.skillKeyHint")}</p>
                        {overridesSystem(key) && (
                            <span><Pill tone="accent">{appState.t("profile.skillOverridesSystem")}</Pill></span>
                        )}
                    </>
                )}
            </Field>
            <Field label={appState.t("profile.skillCommand")}>
                <TextField value={command} onChange={setCommand} placeholder={key} />
            </Field>
            <Field label={appState.t("profile.skillUseCase")}>
                <TextField value={useCase} onChange={setUseCase} />
            </Field>
            <Field label={appState.t("profile.skillPrompt")}>
                <textarea
                    className="textarea min-h-[160px]"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    autoFocus={skill !== null}
                />
            </Field>
            <div className="flex flex-row items-center justify-between gap-2">
                <small className="field-label">{appState.t("profile.skillCreatesNote")}</small>
                <button
                    type="button"
                    role="switch"
                    aria-checked={createsNote}
                    aria-label={appState.t("profile.skillCreatesNote")}
                    onClick={() => setCreatesNote(!createsNote)}
                    className={`toggle ${createsNote ? "toggle-on" : ""}`}
                />
            </div>
            {createsNote && (
                <>
                    <Field label={appState.t("profile.skillNoteTitle")}>
                        <TextField value={noteTitle} onChange={setNoteTitle} />
                    </Field>
                    <Field label={appState.t("profile.skillNoteContent")}>
                        <textarea
                            className="textarea min-h-[80px]"
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                        />
                    </Field>
                </>
            )}
            <div className="flex flex-row justify-end gap-1">
                <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={loading}>
                    {appState.t("common.cancel")}
                </button>
                <button className="btn btn-soft btn-sm" onClick={() => save()} disabled={loading || !valid}>
                    {loading ? appState.t("common.saving") : skill ? appState.t("common.update") : appState.t("common.create")}
                </button>
            </div>
        </div>
    );
});

export const SkillsSection = observer(() => {
    const [loading, setLoading] = useState(false);
    const refresh = () => appState.getUserSkills();

    const openEditor = (skill: UserSkill | null) => {
        appState.appendModal({
            title: skill ? appState.t("profile.editSkill") : appState.t("profile.newSkill"),
            body: <SkillEditor skill={skill} onClose={() => appState.popModal()} onSaved={refresh} />,
        });
    };

    const confirmDelete = (skill: UserSkill) => {
        appState.setModal({
            title: appState.t("profile.deleteSkill"),
            body: appState.t("profile.deleteSkillBody"),
            action: async () => {
                const doDelete = Request<DeleteUserSkillData, any>(
                    deleteUserSkill,
                    () => ({ body: { id: skill.id } }),
                    setLoading,
                    () => refresh(),
                    showError,
                    () => true,
                );
                await doDelete(undefined as any);
            },
        });
    };

    return (
        <Section
            title={appState.t("profile.skills")}
            hint={appState.t("profile.skillsHint")}
            action={
                <ActionHandler
                    className="btn-ghost btn-sm"
                    title={appState.t("profile.addSkill")}
                    funq={() => openEditor(null)}
                    disabled={loading}
                >
                    <Add_new size={16} />
                </ActionHandler>
            }
        >
            {appState.userSkills.length === 0 && (
                <EmptyRow>{appState.t(loading ? "common.loading" : "profile.noSkills")}</EmptyRow>
            )}
            {appState.userSkills.map((skill) => (
                <Row
                    key={skill.id}
                    roomy
                    onClick={() => openEditor(skill)}
                    trailing={
                        <span className="reveal shrink-0" onClick={(e) => e.stopPropagation()}>
                            <ActionHandler
                                className="btn-ghost btn-sm"
                                title={appState.t("profile.deleteSkill")}
                                funq={() => confirmDelete(skill)}
                                disabled={loading}
                            >
                                <Remove size={16} />
                            </ActionHandler>
                        </span>
                    }
                >
                    <div className="flex flex-row flex-wrap items-center gap-1">
                        <p className="b">{skill.command}</p>
                        <code className="code-chip code-chip-muted">{skill.skill_key}</code>
                        {skill.overrides_system && <Pill tone="accent">{appState.t("profile.skillOverridesSystem")}</Pill>}
                    </div>
                    {skill.use_case && <p className="detail-text mt-[.35rem]">{skill.use_case}</p>}
                </Row>
            ))}
        </Section>
    );
});
